import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrder, getAddresses, addAddress } from '../api/orderApi';
import { isLoggedIn } from '../utils/tokenUtils';
import { getCartItems } from '../api/cartApi';
import { getSettings } from '../api/settingsApi';
import styles from './OrderPage.module.css';
import { formatPhone } from '../utils/phoneUtils';
import { useModal } from '../context/ModalContext';
import { useLocation } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function OrderPage() {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [freeDeliveryMin, setFreeDeliveryMin] = useState(50000);
    const [addressForm, setAddressForm] = useState({
        recipient: '', phone: '', zipcode: '', address1: '', address2: '', isDefault: false
    });
    const { showAlert } = useModal();
    const location = useLocation();

    useEffect(() => {
        if (!isLoggedIn()) {
        navigate('/login', { state: { from: location.pathname } });
        return;
    }
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [cartRes, addressRes, settingsRes] = await Promise.all([
                getCartItems(),
                getAddresses(),
                getSettings()
            ]);
            setCartItems(cartRes.data);
            setAddresses(addressRes.data);
            const defaultAddress = addressRes.data.find(a => a.isDefault);
            if (defaultAddress) setSelectedAddressId(defaultAddress.addressId);

            const settings = settingsRes.data;
            const fee = parseInt(settings.delivery_fee || '3000');
            const freeMin = parseInt(settings.free_delivery_min || '50000');
            setDeliveryFee(fee);
            setFreeDeliveryMin(freeMin);
        } catch (err) {
            console.error('데이터 로딩 실패', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddressFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setAddressForm({ ...addressForm, [name]: type === 'checkbox' ? checked : value });
    };

    const handleSearchAddress = () => {
        new window.daum.Postcode({
            oncomplete: (data) => {
                setAddressForm(prev => ({
                    ...prev,
                    zipcode: data.zonecode,
                    address1: data.roadAddress || data.jibunAddress
                }));
            }
        }).open();
    };

    const handleAddAddress = async (e) => {
        e.preventDefault();
        try {
            const response = await addAddress(addressForm);
            setAddresses([...addresses, response.data]);
            setSelectedAddressId(response.data.addressId);
            setShowAddressForm(false);
            setAddressForm({ recipient: '', phone: '', zipcode: '', address1: '', address2: '', isDefault: false });
        } catch (err) {
            await showAlert('배송지 추가에 실패했습니다.', 'error');
        }
    };

    const handleOrder = async () => {
        if (!selectedAddressId) {
            await showAlert('배송지를 선택해주세요.', 'error');
            return;
        }
        try {
            const tossPayments = window.TossPayments('test_ck_5OWRapdA8dzK0vn5mGOYVo1zEqZK');
            await tossPayments.requestPayment('카드', {
                amount: finalPrice,
                orderId: `order-${Date.now()}`,
                orderName: cartItems[0].productName + (cartItems.length > 1 ? ` 외 ${cartItems.length - 1}건` : ''),
                successUrl: `${window.location.origin}/payment/success?addressId=${selectedAddressId}`,
                failUrl: `${window.location.origin}/payment/fail`,
            });
        } catch (err) {
            if (err.code !== 'USER_CANCEL') {
                await showAlert('결제에 실패했습니다.', 'error');
            }
        }
    };

    const productTotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);

    // 상품별 배송비가 있는 경우 최대값, 없으면 기본 정책 적용
    const calcDeliveryFee = () => {
        const hasCustomFee = cartItems.some(item => item.deliveryFee != null);
        if (hasCustomFee) {
            return Math.max(...cartItems.map(item => item.deliveryFee ?? deliveryFee));
        }
        return (freeDeliveryMin > 0 && productTotal >= freeDeliveryMin) ? 0 : deliveryFee;
    };

    const appliedDeliveryFee = calcDeliveryFee();
    const finalPrice = productTotal + appliedDeliveryFee;

    if (loading) return <p className={styles.loading}>로딩 중...</p>;

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>주문하기</h2>

            <div className={styles.orderLayout}>
                <div className={styles.leftSection}>
                    {/* 배송지 선택 */}
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>배송지</h3>
                        {addresses.length === 0 ? (
                            <p className={styles.noAddress}>등록된 배송지가 없습니다.</p>
                        ) : (
                            <div className={styles.addressList}>
                                {addresses.map(address => (
                                    <div
                                        key={address.addressId}
                                        className={`${styles.addressCard} ${selectedAddressId === address.addressId ? styles.selected : ''}`}
                                        onClick={() => setSelectedAddressId(address.addressId)}>
                                        <p className={styles.recipient}>
                                            {address.recipient}
                                            {address.isDefault && <span className={styles.defaultBadge}>기본</span>}
                                        </p>
                                        <p className={styles.addressText}>{address.address1} {address.address2}</p>
                                        <p className={styles.phone}>{address.phone}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button className={styles.addAddressButton}
                            onClick={() => setShowAddressForm(!showAddressForm)}>
                            + 새 배송지 추가
                        </button>

                        {showAddressForm && (
                            <form onSubmit={handleAddAddress} className={styles.addressForm}>
                                <input name="recipient" placeholder="수령인" value={addressForm.recipient}
                                    onChange={handleAddressFormChange} className={styles.input} required />
                                <input
                                    name="phone"
                                    placeholder="연락처 (010-0000-0000)"
                                    value={addressForm.phone}
                                    onChange={e => setAddressForm(prev => ({
                                        ...prev, phone: formatPhone(e.target.value)
                                    }))}
                                    className={styles.input}
                                    maxLength={13}
                                    required />
                                <div className={styles.zipcodeRow}>
                                    <input name="zipcode" placeholder="우편번호" value={addressForm.zipcode}
                                        onChange={handleAddressFormChange} className={styles.input} readOnly required />
                                    <button type="button" onClick={handleSearchAddress}
                                        className={styles.searchButton}>주소 검색</button>
                                </div>
                                <input name="address1" placeholder="주소" value={addressForm.address1}
                                    onChange={handleAddressFormChange} className={styles.input} readOnly required />
                                <input name="address2" placeholder="상세주소" value={addressForm.address2}
                                    onChange={handleAddressFormChange} className={styles.input} />
                                <label className={styles.checkboxLabel}>
                                    <input type="checkbox" name="isDefault" checked={addressForm.isDefault}
                                        onChange={handleAddressFormChange} />
                                    기본 배송지로 설정
                                </label>
                                <button type="submit" className={styles.submitButton}>저장</button>
                            </form>
                        )}
                    </div>

                    {/* 주문 상품 */}
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>주문 상품</h3>
                        {cartItems.map(item => (
                            <div key={item.cartId} className={styles.orderItem}>
                                <img src={`${API_URL}${item.imageUrl}`}
                                    alt={item.productName} className={styles.itemImage} />
                                <div className={styles.itemInfo}>
                                    <p className={styles.itemName}>{item.productName}</p>
                                    <p className={styles.itemQuantity}>{item.quantity}개</p>
                                </div>
                                <p className={styles.itemPrice}>{item.totalPrice.toLocaleString()}원</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 결제 요약 */}
                <div className={styles.rightSection}>
                    <div className={styles.summary}>
                        <h3 className={styles.sectionTitle}>결제 금액</h3>
                        <div className={styles.priceRow}>
                            <span>상품 금액</span>
                            <span>{productTotal.toLocaleString()}원</span>
                        </div>
                        <div className={styles.priceRow}>
                            <span>배송비</span>
                            <span>
                                {appliedDeliveryFee === 0
                                    ? '무료'
                                    : `${appliedDeliveryFee.toLocaleString()}원`}
                            </span>
                        </div>
                        {freeDeliveryMin > 0 && appliedDeliveryFee > 0 && (
                            <p className={styles.freeDeliveryNotice}>
                                {(freeDeliveryMin - productTotal).toLocaleString()}원 더 구매 시 무료배송
                            </p>
                        )}
                        <hr className={styles.divider} />
                        <div className={styles.totalRow}>
                            <span>총 결제금액</span>
                            <span>{finalPrice.toLocaleString()}원</span>
                        </div>
                        <button className={styles.orderButton} onClick={handleOrder}>
                            결제하기
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrderPage;